import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import OrderTotal from './OrderTotal';
import {
  selectBuyCashBufferPercentage,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import {
  buyProductForDelivery,
  verifyBuyForDelivery,
} from '../../store/actions/orders/orderActions';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import ReviewBar from '../ReviewBar/ReviewBar';
import ModalBody from '../Modal/ModalBody';
import Label from '../Notes/Label';
import {
  // onConfirmOrderGtmHelper,
  verifyOrderEcommerceGtmHelper,
} from '../../util/helpers/gtmHelper';
import useGtmHook from '../../util/hooks/useGtmHook';
import useProductPrice from '../../util/hooks/useProductPrice';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { BUY_PRODUCT_FOR_DELIVERY_LOADING } from '../../store/actions/orders/orderActionConstants';
import SectionLoader from '../Loader/SectionLoader';
import { selectBalancesAvailableForTrading } from '../../store/selectors/portfolioBalancesSelectors';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import ModalButtons from '../Modal/ModalButtons';
import DelayedSettlementWarning from '../DelayedSettlementWarning/DelayedSettlementWarning';
import CountdownTimer from '../Common/Timers/CountdownTimer';
import Paragraph from '../Paragraph/Paragraph';
import AnchorOnClick from '../Anchor/AnchorOnClick';
import LockedPriceVariableWeightDisclaimerModal from '../Modals/BuyWizard/LockedPriceVariableWeightDisclaimerModal';
// import { PURCHASE } from '../../constants/gtmEvents';

const ReviewOrderForDelivery = ({
  wizardData,
  setWizardData,
  handleBack,
  handleNext,
  setWizardTitle,
  counter,
  unity,
  tiers,
}) => {
  const {
    // gtmCustomEvent,
    // gtmEvent,
    ecommerceGtmEvent,
  } = useGtmHook();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const buyCashBuffer = useSelector(selectBuyCashBufferPercentage);
  const balanceAvailableForTrading = useSelector(
    selectBalancesAvailableForTrading,
  );
  const [
    balanceAvailableForTradingIsValid,
    setBalanceAvailableForTradingIsValid,
  ] = useState(true);
  const hasDelayedSettlement = wizardData?.selectedVault?.HasDelayedSettlement;

  const account = useSelector(getCurrentAccount);
  const unitPrice = useProductPrice(
    wizardData?.storageProduct,
    wizardData?.selectedVault,
  );
  const isLoading = useSelector(
    selectIsLoadingByActionType(BUY_PRODUCT_FOR_DELIVERY_LOADING),
  );
  const [finalOrder, setFinalOrder] = useState({});
  const [lockedPriceActive, setLockedPriceActive] = useState(false);
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [pricePerUnit, setPricePerUnit] = useState(unitPrice);
  const settings = useSelector(selectSettings);
  const [deliveryFeesError, setDeliveryFeesError] = useState('');

  const {
    storageProduct,
    orderValues,
    selectedVault,
    orderType,
    calculation,
    deliveryAddress,
    activeType,
  } = wizardData;

  const {
    volumeDiscount,
    newPricePerUnit,
    salesTax,
    unassessedStorageFees,
  } = calculation;
  const { CashAmount, QuantityAmount } = orderValues;

  const calculateQuantityForCashOrder = useMemo(
    () => Math.floor(CashAmount / newPricePerUnit),
    [newPricePerUnit, CashAmount],
  );

  useEffect(() => {
    if (
      !settings ||
      !settings?.BrokerCode ||
      isEmpty(finalOrder) ||
      !balanceAvailableForTrading
    ) {
      return;
    }
    setLockedPriceActive(settings.IsLockedPricingActive);
    const { requiredBalanceWithBuffer, estimatedTotal } = finalOrder;
    const isValid =
      activeType === 'Quantity' && !settings.IsLockedPricingActive
        ? balanceAvailableForTrading >= requiredBalanceWithBuffer
        : balanceAvailableForTrading >= estimatedTotal;

    setBalanceAvailableForTradingIsValid(isValid);
  }, [activeType, balanceAvailableForTrading, finalOrder, settings]);

  useEffect(() => {
    if (counter) {
      return;
    }

    const onSuccess = () => {
      // setDeliveryFees({ ...deliveryOrderFees });
      setWizardData({ ...wizardData });
    };

    const onError = (errorMessage) => {
      setDeliveryFeesError(errorMessage);
    };

    dispatch(
      verifyBuyForDelivery({
        accountUid: account.AccountUid,
        data: {
          AccountUid: account.AccountUid,
          QuantityAmount: CashAmount || QuantityAmount,
          AddressUid: deliveryAddress?.AddressUid,
          Type: 'BuyForDelivery',
          SymbolCode: selectedVault?.SymbolCode,
          UnitType: orderType,
        },
        onSuccess,
        onError,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter]);

  useEffect(() => {
    if (!selectedVault || !orderValues) {
      return;
    }

    setFinalOrder(() => ({
      quantity:
        orderType === 'Cash' ? calculateQuantityForCashOrder : QuantityAmount,
      unitPrice: calculation?.newPricePerUnit || pricePerUnit,
      subtotal: calculation?.estimatedSubTotal,
      deliveryFees: calculation?.deliveryCosts,
      salesTax,
      unassessedStorageFees,
      estimatedTotal: calculation?.estimatedOrderTotal,
      cashBuffer: calculation?.cashBuffer,
      requiredBalanceWithBuffer: calculation?.estimatedOrderTotalWithCashBuffer,
      volumeDiscount,
      newPricePerUnit,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderValues,
    orderType,
    buyCashBuffer,
    unitPrice,
    lockedPriceActive,
    wizardData,
    tiers,
  ]);

  useEffect(() => {
    setWizardTitle(t('buyWizard.verifyOrder.buyForDelivery'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (account.AccountUid) {
      dispatch(fetchPortfolioBalances({ accountUid: account.AccountUid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.AccountUid]);

  useEffect(() => {
    setPricePerUnit(wizardData?.calculation?.newPricePerUnit);
  }, []); // eslint-disable-line

  const onHandleSuccess = (orderCode) => {
    // onConfirmOrderGtmHelper({
    //   gtmEvent,
    //   gtmCustomEvent,
    //   wizardData,
    //   finalOrder,
    //   account,
    //   isForDelivery: true,
    //   orderCode,
    // });
    verifyOrderEcommerceGtmHelper({
      ecommerceGtmEvent,
      wizardData,
      finalOrder,
      account,
      orderSideType: 'buy',
      quantity:
        orderType === 'Cash'
          ? parseFloat(calculateQuantityForCashOrder?.toFixed(2))
          : parseFloat(orderValues?.QuantityAmount),
      isForDelivery: true,
      orderCode,
    });
  };

  const onConfirmOrder = () => {
    setWizardTitle(t('buyWizard.buyForDelivery.title'));
    handleNext();
  };

  const handleConfirmOrder = () => {
    const payload = {
      reviewProduct: {
        UnitType: orderType,
        QuantityAmount:
          orderType === 'Cash'
            ? orderValues.CashAmount
            : orderValues.QuantityAmount,
        SymbolCode: selectedVault?.SymbolCode,
        AccountUid: account?.AccountUid,
        Type: 'BuyForDelivery',
        AddressUid: deliveryAddress?.AddressUid,
        UseLockedPrice: settings?.IsLockedPricingActive ?? false,
      },
      onConfirmOrder,
      onHandleSuccess,
    };

    dispatch(
      buyProductForDelivery({
        ...payload,
      }),
    );
  };

  const closeDisclaimerModal = () => {
    setIsDisclaimerModalOpen(false);
  };

  if (isDisclaimerModalOpen) {
    return (
      <LockedPriceVariableWeightDisclaimerModal
        isOpen={isDisclaimerModalOpen}
        close={closeDisclaimerModal}
        goBack={closeDisclaimerModal}
      />
    );
  }

  return (
    <SectionLoader isLoading={isLoading}>
      <ReviewBar title={t('buyWizard.verifyOrder.review')} />
      <ModalBody>
        <h2>{`${storageProduct?.Caption} ${startCase(
          parseEnumType(productLocationTypes, selectedVault?.LocationType),
        )}`}</h2>
        <Label text={t('buyWizard.verifyOrder.subtitle')} />
        {!isEmpty(finalOrder) && (
          <OrderTotal
            orderType={orderType}
            orderValues={orderValues}
            deliveryAddress={deliveryAddress}
            finalOrder={finalOrder}
            buyCashBuffer={buyCashBuffer}
            balanceAvailableForTradingIsValid={
              balanceAvailableForTradingIsValid
            }
            isLockedPricingActive={lockedPriceActive}
          />
        )}
        {hasDelayedSettlement && <DelayedSettlementWarning marginTop={20} />}
        <CountdownTimer counter={counter} unity={unity} />

        {settings?.IsLockedPricingActive && !storageProduct?.IsFixedWeight && (
          <Paragraph fontSize={14} marginTop={12}>
            {t('lockedPrice.disclaimerInfo')}
            <AnchorOnClick onClick={() => setIsDisclaimerModalOpen(true)}>
              {t('lockedPrice.disclaimerInfoLink')}
            </AnchorOnClick>
          </Paragraph>
        )}
        {deliveryFeesError && (
          <Paragraph isError fontSize={14} marginTop={12}>
            {t('lockedPrice.deliveryFeesError')}
          </Paragraph>
        )}

        <ModalButtons
          isHorizontal
          marginTop
          secondaryButtonProps={{ onClick: handleBack }}
          primaryButtonProps={{
            label: t('common.confirm'),
            onClick: handleConfirmOrder,
            disabled: !balanceAvailableForTradingIsValid || deliveryFeesError,
          }}
        />
      </ModalBody>
    </SectionLoader>
  );
};

ReviewOrderForDelivery.propTypes = {
  wizardData: PropTypes.shape({
    activeType: PropTypes.string,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
      HasDelayedSettlement: PropTypes.bool,
    }),
    orderValues: PropTypes.shape({
      QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      CashAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    storageProduct: PropTypes.shape({
      Caption: PropTypes.string,
      IsFixedWeight: PropTypes.bool,
    }),
    orderType: PropTypes.string,
    calculation: PropTypes.shape({
      estimatedSubTotal: PropTypes.number,
      deliveryCosts: PropTypes.number,
      salesTax: PropTypes.number,
      unassessedStorageFees: PropTypes.number,
      newPricePerUnit: PropTypes.number,
      volumeDiscount: PropTypes.number,
      estimatedOrderTotal: PropTypes.number,
      cashBuffer: PropTypes.number,
      estimatedOrderTotalWithCashBuffer: PropTypes.number,
    }),
    deliveryAddress: PropTypes.shape({
      AddressUid: PropTypes.string,
    }),
    deliveryOrderFees: PropTypes.shape({
      deliveryCosts: PropTypes.number,
      salesTax: PropTypes.number,
      unassessedStorageFees: PropTypes.number,
    }),
    volumeDiscount: PropTypes.number,
    newPricePerUnit: PropTypes.number,
  }),
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  counter: PropTypes.number,
  unity: PropTypes.bool,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      HighBound: PropTypes.number,
      LowBound: PropTypes.number,
      PricePerOz: PropTypes.number,
      PricePerUnit: PropTypes.number,
    }),
  ),
};

export default ReviewOrderForDelivery;
